import React, { useEffect, useMemo, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import AnalyticsAPI from "../services/AnalyticsAPI";
import { viewerPanelEyebrowSx } from "./unityWebViewer/panelStyles";

// ============================================
// UTILITY FUNCTIONS
// ============================================

const safeString = (v) => {
  if (v == null) return "";
  return typeof v === "string" ? v : String(v);
};

const safeJsonParse = (v) => {
  try {
    if (v == null) return null;
    if (typeof v === "object") return v;
    return JSON.parse(String(v));
  } catch {
    return null;
  }
};

const getEventType = (pk) => {
  const s = safeString(pk);
  if (!s) return "";
  return s.split("#")[0] || "";
};

const shouldRenderInTimeline = (ev) => {
  const type = getEventType(ev?.pk);
  return (
    type === "EnvironmentVisited" ||
    type === "ElementExpanded" ||
    type === "Assessment" ||
    type === "GuideStarted" ||
    type === "GuideEnded"
  );
};

const formatTime = (timestamp) => {
  if (!timestamp) return "";
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "";
  }
};

const getEventStyle = (type) => {
  switch (type) {
    case "EnvironmentVisited":
      return {
        color: "#4ade80",
        bgColor: "rgba(74, 222, 128, 0.15)",
        label: "Environment visited",
      };
    case "ElementExpanded":
      return {
        color: "#60a5fa",
        bgColor: "rgba(96, 165, 250, 0.15)",
        label: "Description expanded",
      };
    case "Assessment":
      return {
        color: "#f472b6",
        bgColor: "rgba(244, 114, 182, 0.15)",
        label: "Insight answered",
      };
    case "GuideStarted":
      return {
        color: "#a78bfa",
        bgColor: "rgba(167, 139, 250, 0.15)",
        label: "Guide started",
      };
    case "GuideEnded":
      return {
        color: "#fbbf24",
        bgColor: "rgba(251, 191, 36, 0.15)",
        label: "Guide ended",
      };
    default:
      return {
        color: "#94a3b8",
        bgColor: "rgba(148, 163, 184, 0.15)",
        label: type || "Event",
      };
  }
};

// ============================================
// TIMELINE TOGGLE BUTTON ICON
// ============================================

const TimelineIcon = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {/* Event Stack icon */}
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2L2 7l10 5 10-5-10-5z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M2 12l10 5 10-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 17l10 5 10-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

// ============================================
// TIMELINE EVENT COMPONENT
// ============================================

const TimelineEvent = ({ data, isNewest, environmentLookup = {}, elementLookup = {} }) => {
  const type = getEventType(data?.pk);
  const style = getEventStyle(type);
  const timestamp = data?.sk || data?.timestamp || data?.createdAt;

  const getEnvironmentInfo = (envId) => {
    const id = safeString(envId);
    if (environmentLookup[id]) {
      return environmentLookup[id];
    }
    return { name: id, thumbnail: null };
  };

  const getElementInfo = (elemId) => {
    const id = safeString(elemId);
    if (elementLookup[id]) {
      return elementLookup[id];
    }
    return { name: id };
  };

  const getSubtitle = () => {
    if (type === "EnvironmentVisited") {
      const envId = data?.environmentName || data?.value || "Unknown";
      const envInfo = getEnvironmentInfo(envId);
      return envInfo.name.replace(/_/g, " ");
    }
    if (type === "ElementExpanded") {
      const elemId = data?.elementName || data?.value || "Unknown";
      const elemInfo = getElementInfo(elemId);
      return elemInfo.name.replace(/_/g, " ");
    }
    if (type === "Assessment") {
      const parsed = safeJsonParse(data?.value);
      const question = safeString(parsed?.text || parsed?.question || data?.question || "");
      const answer = safeString(data?.transcript || data?.studentAnswer || "");
      return { question, answer };
    }
    if (type === "GuideStarted" || type === "GuideEnded") {
      return data?.guideName || data?.value || "";
    }
    return data?.value || "";
  };

  const subtitle = getSubtitle();

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        py: 0.75,
        px: 1,
        borderRadius: "8px",
        backgroundColor: isNewest ? style.bgColor : "rgba(255, 255, 255, 0.03)",
        border: isNewest ? `1px solid ${style.color}30` : "1px solid transparent",
        transition: "all 0.3s ease",
        animation: isNewest ? "fadeIn 0.4s ease" : "none",
        "@keyframes fadeIn": {
          "0%": { opacity: 0, transform: "translateY(-8px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {/* Colored dot
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: style.color,
          flexShrink: 0,
          mt: 0.6,
        }}
      /> */}

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "rgba(255, 255, 255, 0.5)",
            }}
          >
            {style.label}
          </Typography>
          {timestamp && (
            <Typography
              sx={{
                fontSize: "0.65rem",
                color: "rgba(255, 255, 255, 0.35)",
                fontFamily: "monospace",
              }}
            >
              {formatTime(timestamp)}
            </Typography>
          )}
        </Box>
        
        {type === "Assessment" ? (
          <Box sx={{ mt: 0.25 }}>
            {subtitle.question && (
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "rgba(255, 255, 255, 0.5)",
                  lineHeight: 1.3,
                  fontStyle: "italic",
                }}
              >
                Q: {subtitle.question.slice(0, 50)}{subtitle.question.length > 50 ? "..." : ""}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                color: subtitle.answer ? style.color : "rgba(255, 255, 255, 0.4)",
                lineHeight: 1.35,
                mt: 0.15,
                wordBreak: "break-word",
              }}
            >
              {subtitle.answer 
                ? `"${subtitle.answer.slice(0, 80)}${subtitle.answer.length > 80 ? "..." : ""}"` 
                : "No response yet"}
            </Typography>
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: 600,
              color: style.color,
              lineHeight: 1.35,
              mt: 0.15,
              wordBreak: "break-word",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ============================================
// TOGGLE BUTTON COMPONENT (exported separately)
// ============================================

export const SessionTimelineToggleButton = ({ 
  isOpen, 
  onClick,
  highlight = false,
}) => {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={isOpen ? "Hide session timeline" : "Show session timeline"}
      sx={{
        width: 44,
        height: 44,
        padding: 0,
        border: "none",
        borderRadius: "12px",
        backgroundColor: "var(--surface-bg, rgba(9, 12, 18, 0.62))",
        boxShadow: (highlight || isOpen)
          ? "0 0 20px rgb(0, 153, 255), 0 0 40px rgba(89, 219, 230, 0.2), 0 14px 34px rgba(0, 0, 0, 0.34)"
          : "var(--surface-shadow, 0 14px 34px rgba(0, 0, 0, 0.34))",
        backdropFilter: "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
        WebkitBackdropFilter: "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
        color: "rgba(255, 255, 255, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.3s ease",
        pointerEvents: "auto",
        position: "relative",
        overflow: "visible",
        "&:hover": {
          boxShadow: "0 0 20px rgb(89, 218, 230), 0 0 40px rgb(89, 218, 230), 0 14px 34px rgba(0, 0, 0, 0.34)",
        },
        "&:active": {
          transform: "scale(0.98)",
        },
        // Pulse animation for soft glow
        ...(highlight && !isOpen && {
          animation: "highlightGlow 2s ease-in-out infinite",
          "@keyframes highlightGlow": {
            "0%, 100%": { 
              boxShadow: "0 0 15px rgba(89, 218, 230, 0.84), 0 0 30px rgba(89, 219, 230, 0.15), 0 14px 34px rgba(0, 0, 0, 0.34)",
              transform: "scale(1)",
            },
            "50%": { 
              boxShadow: "0 0 25px rgb(21, 235, 255), 0 0 50px rgba(89, 219, 230, 0.25), 0 14px 34px rgba(0, 0, 0, 0.34)",
              transform: "scale(1.1)",
            },
          },
        }),
      }}
    >
      <TimelineIcon />
    </Box>
  );
};

// ============================================
// MAIN PANEL COMPONENT
// ============================================

const SessionTimelinePanel = ({
  sessionId = null,
  environmentLookup = {},
  elementLookup = {},
  localEvents = [],
  maxHeight = "320px",
  pollInterval = 5000,
  isOpen = true,
  onClose,
}) => {
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const prevEventCountRef = useRef(0);
  const [newestEventId, setNewestEventId] = useState(null);

  const fetchSessionData = async (isInitial = false) => {
    if (!sessionId) return;

    if (isInitial) {
      setLoading(true);
    }

    try {
      const response = await AnalyticsAPI.get(`sessions/${sessionId}`);
      const data = response?.data;
      setSessionData(data);

      const events = buildTimelineEvents(data, localEvents);
      if (events.length > prevEventCountRef.current && !isInitial) {
        setNewestEventId(events[0]?.id);
        setTimeout(() => setNewestEventId(null), 2000);
      }
      prevEventCountRef.current = events.length;
    } catch (error) {
      console.error("Failed to load session data", error);
      if (isInitial) {
        setSessionData(null);
      }
    } finally {
      if (isInitial) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!sessionId) {
      setSessionData(null);
      setLoading(false);
      prevEventCountRef.current = 0;
      return;
    }

    fetchSessionData(true);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;

    const interval = setInterval(() => {
      fetchSessionData(false);
    }, pollInterval);

    return () => clearInterval(interval);
  }, [sessionId, pollInterval]);

  const buildTimelineEvents = (data, localEvts = []) => {
    if (!data && (!localEvts || localEvts.length === 0)) return [];

    const eventArray =
      (Array.isArray(data) && data) ||
      (Array.isArray(data?.events) && data.events) ||
      (Array.isArray(data?.eventLog) && data.eventLog) ||
      (Array.isArray(data?.logs) && data.logs) ||
      [];

    const allEvents = [...eventArray, ...localEvts];

    return allEvents
      .filter(shouldRenderInTimeline)
      .map((ev, idx) => ({
        ...ev,
        id: ev.id || `${ev.pk || "event"}-${ev.sk || idx}-${idx}`,
      }))
      .sort((a, b) => {
        const timeStrA = a.sk || a.timestamp || a.createdAt || "";
        const timeStrB = b.sk || b.timestamp || b.createdAt || "";
        const dateA = new Date(timeStrA).getTime() || 0;
        const dateB = new Date(timeStrB).getTime() || 0;
        return dateB - dateA;
      });
  };

  const timelineEvents = useMemo(
    () => buildTimelineEvents(sessionData, localEvents), 
    [sessionData, localEvents]
  );

  const stats = useMemo(() => {
    const envCount = timelineEvents.filter((e) => getEventType(e.pk) === "EnvironmentVisited").length;
    const descCount = timelineEvents.filter((e) => getEventType(e.pk) === "ElementExpanded").length;
    const insightCount = timelineEvents.filter((e) => getEventType(e.pk) === "Assessment").length;
    return { envCount, descCount, insightCount };
  }, [timelineEvents]);

  if (!isOpen) return null;

  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: "var(--surface-bg, rgba(9, 12, 18, 0.62))",
        boxShadow: "var(--surface-shadow, 0 14px 34px rgba(0, 0, 0, 0.34))",
        borderRadius: "var(--radius-lg, 14px)",
        backdropFilter: "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
        WebkitBackdropFilter: "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
        color: "#fff",
        pointerEvents: "auto",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        animation: "slideIn 0.25s ease-out",
        "@keyframes slideIn": {
          "0%": { opacity: 0, transform: "translateY(-10px) scale(0.98)" },
          "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="overline"
            sx={{
              ...viewerPanelEyebrowSx,
              opacity: 0.74,
            }}
          >
            Session Timeline
          </Typography>
          {timelineEvents.length > 0 && (
            <Box
              sx={{
                px: 0.75,
                py: 0.15,
                borderRadius: "10px",
                backgroundColor: "rgba(74, 222, 128, 0.15)",
                fontSize: "0.65rem",
                fontWeight: 600,
                color: "#4ade80",
              }}
            >
              {timelineEvents.length}
            </Box>
          )}
        </Box>
      </Box>

      {/* Stats bar */}
      {timelineEvents.length > 0 && (
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            display: "flex",
            gap: 1.5,
            justifyContent: "space-around",
            borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
          }}
        >
          <Box sx={{ textAlign: "center", flex: 1 }}>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#4ade80" }}>
              {stats.envCount}
            </Typography>
            <Typography sx={{ fontSize: "0.5rem", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.03em" }}>
              Environments
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center", flex: 1 }}>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#60a5fa" }}>
              {stats.descCount}
            </Typography>
            <Typography sx={{ fontSize: "0.5rem", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.03em" }}>
              Descriptions
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center", flex: 1 }}>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#f472b6" }}>
              {stats.insightCount}
            </Typography>
            <Typography sx={{ fontSize: "0.5rem", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.03em" }}>
              Insights
            </Typography>
          </Box>
        </Box>
      )}

      {/* Timeline events */}
      <Box
        sx={{
          maxHeight: maxHeight,
          overflowY: "auto",
          px: 1,
          py: 0.75,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          "&::-webkit-scrollbar": {
            width: 4,
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255, 255, 255, 0.05)",
            borderRadius: 2,
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.15)",
            borderRadius: 2,
            "&:hover": {
              background: "rgba(255, 255, 255, 0.25)",
            },
          },
        }}
      >
        {loading && timelineEvents.length === 0 && (
          <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", textAlign: "center", py: 2 }}>
            Loading...
          </Typography>
        )}

        {!loading && timelineEvents.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 3,
              opacity: 0.6,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "2px dashed rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.5,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2">
                <path d="M3 12h4l3-9 4 18 3-9h4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Box>
            <Typography sx={{ fontSize: "0.8rem", textAlign: "center", color: "rgba(255,255,255,0.5)" }}>
              {sessionId ? "No events yet" : "Waiting for session..."}
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", textAlign: "center", color: "rgba(255,255,255,0.3)", mt: 0.5 }}>
              Activity will appear here
            </Typography>
          </Box>
        )}

        {timelineEvents.map((ev) => (
          <TimelineEvent
            key={ev.id}
            data={ev}
            isNewest={ev.id === newestEventId}
            environmentLookup={environmentLookup}
            elementLookup={elementLookup}
          />
        ))}
      </Box>
    </Box>
  );
};

export default SessionTimelinePanel;
